import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { pushSheet, popSheet } from '../sheetStack.js'

const ANIM_MS = 200

// Top-sheet: scivola dall'alto, occupa lo spazio disponibile (visualViewport
// height) — quando la keyboard nativa si apre per l'input di ricerca, lo
// sheet si rimpicciolisce automaticamente per non finire sotto la keyboard.
// Outside-click + Esc per chiudere.
export function TopSheet({ open, onClose, children, className = '' }) {
  const [mounted, setMounted] = useState(false)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    if (mounted) {
      const t = setTimeout(() => setMounted(false), ANIM_MS)
      return () => clearTimeout(t)
    }
  }, [open, mounted])

  useLayoutEffect(() => {
    if (open && mounted) {
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    }
    setEntered(false)
  }, [open, mounted])

  // Mark body.has-sheet while mounted so layout-aware pages (e.g. Converter
  // hides its chart) can react. Same metaphor as BottomSheet.
  useEffect(() => {
    if (!mounted) return
    pushSheet()
    return () => popSheet()
  }, [mounted])

  // Track visualViewport so the sheet shrinks when the native keyboard opens
  useEffect(() => {
    if (!mounted) return
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      document.documentElement.style.setProperty('--vv-h', `${vv.height}px`)
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [mounted])

  // Stable ref for onClose so the effects below don't re-run on every parent
  // render (which would cause a pushState/back loop, opening + closing).
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Outside click
  useEffect(() => {
    if (!open) return
    function onPointerDown(e) {
      const sheets = document.querySelectorAll('.tsheet')
      for (const el of sheets) {
        if (el.contains(e.target)) return
      }
      onCloseRef.current()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [open])

  // Esc
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') onCloseRef.current() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Back-button (Android / browser) closes the sheet instead of navigating
  const closedByPopRef = useRef(false)
  useEffect(() => {
    if (!open) return
    history.pushState({ sheet: true }, '')
    closedByPopRef.current = false
    function onPop() {
      closedByPopRef.current = true
      onCloseRef.current()
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      // If we closed via X / item / outside-click, consume our history entry
      if (!closedByPopRef.current && history.state && history.state.sheet) {
        history.back()
      }
    }
  }, [open])

  if (!mounted) return null

  return createPortal(
    <div className={`tsheet${entered ? ' tsheet-entered' : ''} ${className}`}>
      {children}
    </div>,
    document.body,
  )
}
